import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PeptideGPT from '../components/PeptideGPT';
import { ArrowRight, BookOpen, FlaskConical, GraduationCap, TrendingUp, Sparkles } from 'lucide-react';
import { getHeroImage } from '../utils/heroImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOP_URL = 'https://shop.paramountpeptides.com/MARKBECKER';

const PEPTIDE_BG = 'https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/0y16vvao_Peptide%20Background.png';
const PEPTIDE_BG_ALT = 'https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png';

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What happened to Peptide Sciences?", "acceptedAnswer": { "@type": "Answer", "text": "Peptide Sciences (peptidesciences.com) ceased operations in early 2026. PeptideSciences.ai continues as an independent educational platform providing evidence-based peptide research and information." } },
    { "@type": "Question", "name": "Are peptides legal in the United States?", "acceptedAnswer": { "@type": "Answer", "text": "Peptides for research purposes are legal in the United States. However, peptides intended for human consumption may be regulated by the FDA. Always ensure compliance with local and federal regulations." } },
    { "@type": "Question", "name": "What are the most researched peptides?", "acceptedAnswer": { "@type": "Answer", "text": "The most researched peptides include BPC-157 for tissue repair, TB-500 for recovery, GLP-1 agonists (semaglutide) for metabolic health, CJC-1295/Ipamorelin for growth hormone optimization, and Thymosin Alpha-1 for immune support." } },
    { "@type": "Question", "name": "How do I verify peptide purity?", "acceptedAnswer": { "@type": "Answer", "text": "Peptide purity can be verified through third-party lab testing using HPLC (High-Performance Liquid Chromatography) and mass spectrometry. Reputable suppliers provide Certificates of Analysis (COAs) for each batch." } },
    { "@type": "Question", "name": "What is PeptideGPT?", "acceptedAnswer": { "@type": "Answer", "text": "PeptideGPT is an AI-powered research assistant on PeptideSciences.ai that provides evidence-based information about peptides, their mechanisms of action, safety profiles, and clinical research findings." } },
    { "@type": "Question", "name": "Are peptides safe?", "acceptedAnswer": { "@type": "Answer", "text": "Peptide safety varies by compound, dose, and route of administration. Many peptides like BPC-157 and Thymosin Alpha-1 have demonstrated favorable safety profiles in clinical studies. Always consult healthcare professionals and review published research before use." } },
    { "@type": "Question", "name": "What are Peptide Sciences alternatives?", "acceptedAnswer": { "@type": "Answer", "text": "For research-grade peptides, alternatives include Paramount Peptides and other vendors that provide third-party tested, high-purity peptides with Certificates of Analysis. PeptideSciences.ai provides educational resources to help evaluate peptide sources." } },
    { "@type": "Question", "name": "What is the difference between peptides and proteins?", "acceptedAnswer": { "@type": "Answer", "text": "Peptides are short chains of amino acids (typically 2-50), while proteins are longer chains (50+). Peptides are generally smaller, more easily absorbed, and can act as signaling molecules in the body. Many therapeutic peptides mimic natural signaling peptides." } }
  ]
};

const ORG_SCHEMA = { "@context": "https://schema.org", "@type": "Organization", "name": "PeptideSciences.ai", "url": "https://www.peptidesciences.ai", "logo": "https://www.peptidesciences.ai/logo.png", "description": "World's leading peptide research and education platform", "sameAs": ["https://peptidesforcancer.org", "https://peptidesforfertility.org", "https://peptidesdictionary.com"] };

const WEBSITE_SCHEMA = { "@context": "https://schema.org", "@type": "WebSite", "name": "PeptideSciences.ai", "url": "https://www.peptidesciences.ai", "description": "Evidence-based peptide research database with AI chat assistant", "potentialAction": { "@type": "SearchAction", "target": { "@type": "EntryPoint", "urlTemplate": "https://www.peptidesciences.ai/research?q={search_term_string}" }, "query-input": "required name=search_term_string" } };

const HeroSection = ({ onOpenChat }) => (
  <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 text-white py-24 px-4 overflow-hidden">
    <div className="absolute inset-0 opacity-30" style={{backgroundImage: `url(${PEPTIDE_BG})`, backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 via-gray-800/80 to-slate-900/85"></div>
    <div className="max-w-7xl mx-auto relative z-10">
      <div className="max-w-4xl">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">World's Leading Peptide Research Resource</h1>
        <p className="text-xl md:text-2xl mb-8 text-gray-300" data-testid="hero-subtitle">Comprehensive, science-backed information on peptides for health, longevity, and performance optimization.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/research" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl" data-testid="explore-research-btn">
            <BookOpen className="mr-2" size={20} /> Explore Research
          </Link>
          <button onClick={onOpenChat} className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl" data-testid="peptidegpt-btn">
            <Sparkles className="mr-2" size={20} /> Ask PeptideGPT
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all shadow-lg hover:shadow-xl border border-white/20" data-testid="shop-peptides-btn">
            <ArrowRight className="mr-2" size={20} /> Shop Premium Peptides
          </a>
          <a href="https://alphabiomedlabs.com/pages/register?ref=MARKBECKER" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl" data-testid="gpo-signup-btn">
            <ArrowRight className="mr-2" size={20} /> Licensed Provider/GPO Sign Up Here
          </a>
        </div>
      </div>
    </div>
  </section>
);

const StatsSection = () => (
  <section className="py-12 bg-white border-b">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div><div className="text-4xl font-bold text-blue-600 mb-2">64+</div><div className="text-gray-600">Research Articles</div></div>
        <div><div className="text-4xl font-bold text-blue-600 mb-2">32+</div><div className="text-gray-600">Health Categories</div></div>
        <div><div className="text-4xl font-bold text-blue-600 mb-2">100%</div><div className="text-gray-600">Science-Backed</div></div>
        <div><div className="text-4xl font-bold text-blue-600 mb-2">&infin;</div><div className="text-gray-600">Free Access</div></div>
      </div>
    </div>
  </section>
);

const CategoriesGrid = ({ categories }) => (
  <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 text-gray-900">Research by Category</h2>
        <p className="text-lg text-gray-600">Explore our comprehensive peptide research library</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="categories-grid">
        {categories.map((category) => (
          <Link key={category.slug} to={`/research/${category.slug}`} className="p-6 bg-white rounded-lg shadow hover:shadow-lg transition-all hover:scale-105 border border-gray-100" data-testid={`category-${category.slug}`}>
            <h3 className="font-bold text-gray-900 mb-2">{category.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-2">{category.description}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ShopCTA = () => (
  <section className="py-20 px-4 bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-15" style={{backgroundImage: `url(${PEPTIDE_BG_ALT})`, backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
    <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
    <div className="max-w-4xl mx-auto text-center relative z-10">
      <FlaskConical className="mx-auto mb-6" size={64} />
      <h2 className="text-4xl font-bold mb-6">Ready to Experience Premium Peptides?</h2>
      <p className="text-lg mb-8 text-gray-300">Shop our curated selection of research-grade peptides from trusted sources.</p>
      <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-white text-blue-700 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl" data-testid="cta-shop-btn">
        Visit Our Shop <ArrowRight className="ml-2" size={24} />
      </a>
    </div>
  </section>
);

const WhySection = () => (
  <section className="py-20 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16"><h2 className="text-4xl font-bold mb-4 text-gray-900">Why PeptideSciences.ai?</h2></div>
      <div className="grid md:grid-cols-3 gap-12">
        <div className="text-center">
          <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6"><GraduationCap className="text-blue-600" size={40} /></div>
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Expert Research</h3>
          <p className="text-gray-600">Every article is written by PhD researchers and medical professionals with deep peptide expertise.</p>
        </div>
        <div className="text-center">
          <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6"><FlaskConical className="text-blue-600" size={40} /></div>
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Science-Backed</h3>
          <p className="text-gray-600">All information is grounded in peer-reviewed research and clinical studies with full citations.</p>
        </div>
        <div className="text-center">
          <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6"><TrendingUp className="text-blue-600" size={40} /></div>
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Constantly Updated</h3>
          <p className="text-gray-600">New research added regularly to keep you informed of the latest peptide discoveries.</p>
        </div>
      </div>
    </div>
  </section>
);

const ArticleCard = ({ article }) => (
  <Link to={`/article/${article.slug || article.id}`} className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden" data-testid={`article-card-${article.slug || article.id}`}>
    <img src={getHeroImage(article.category)} alt={article.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
    <div className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{article.category.replace(/-/g, ' ')}</span>
        <span className="text-gray-400">&bull;</span>
        <span className="text-xs text-gray-500">{article.read_time} min read</span>
      </div>
      <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">{article.title}</h3>
      <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{article.author}</span>
        <ArrowRight className="text-blue-600 group-hover:translate-x-1 transition-transform" size={20} />
      </div>
    </div>
  </Link>
);

const FeaturedArticles = ({ articles, loading }) => (
  <section className="py-20 px-4 bg-gray-50">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 text-gray-900">Featured Research</h2>
        <p className="text-lg text-gray-600">Latest insights from peptide science</p>
      </div>
      {loading ? (
        <div className="text-center py-12">Loading articles...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-articles-grid">
          {articles.map((article) => <ArticleCard key={article.id} article={article} />)}
        </div>
      )}
      <div className="text-center mt-12">
        <Link to="/research" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all" data-testid="view-all-articles-btn">
          View All Research Articles <ArrowRight className="ml-2" size={18} />
        </Link>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPeptideGPT, setShowPeptideGPT] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/articles/featured?limit=6`),
        axios.get(`${API}/categories`)
      ]);
      setFeaturedArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>{'PeptideSciences.ai | 64+ Evidence-Based Peptide Research Guides (2026)'}</title>
        <meta name="description" content="Comprehensive peptide research database with 64+ articles covering BPC-157, TB-500, GLP-1, CJC-1295, and more. Evidence-based, safety-focused peptide education with AI-powered chat assistant." />
        <link rel="canonical" href="https://www.peptidesciences.ai/" />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="PeptideSciences.ai - World's Leading Peptide Research Resource" />
        <meta property="og:description" content="64+ evidence-based peptide research articles with AI-powered chat assistant. Covering mechanisms, safety, and clinical applications." />
        <meta property="og:url" content="https://www.peptidesciences.ai/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PeptideSciences.ai - Evidence-Based Peptide Research" />
        <meta name="twitter:description" content="AI-powered peptide education with 64+ research articles. Ask PeptideGPT for instant answers." />
        <meta name="keywords" content="peptides, peptide research, BPC-157, TB-500, GLP-1, peptide safety, peptide guide, peptide education, PeptideGPT" />
        <meta property="og:site_name" content="PeptideSciences.ai" />
        <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBSITE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <Navigation />
      <HeroSection onOpenChat={() => setShowPeptideGPT(true)} />
      <StatsSection />
      <CategoriesGrid categories={categories} />
      <ShopCTA />
      <WhySection />
      <FeaturedArticles articles={featuredArticles} loading={loading} />
      <Footer />
      <PeptideGPT isOpen={showPeptideGPT} onClose={() => setShowPeptideGPT(false)} />
    </div>
  );
};

export default HomePage;
